

import React from "react";
import AdminNavBar from "../components/AdminNavBar";
import "bootstrap/dist/css/bootstrap.min.css";
import { faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  FaUsers,
  FaBook,
  FaUserGraduate,
  FaUserFriends,
  FaChartLine,
  FaCheckCircle,
  FaBookOpen,
  FaUser,
  FaLightbulb,
  FaLaptop,
  FaDollarSign,
  FaHourglass
} from "react-icons/fa";
import ExamDashboardView from "./components/examPurchases/ExamDashboardView";
import ExamSystemAlerts from "./components/examPurchases/ExamSystemAlerts";
import RecentPurchasesPanel from "./components/examPurchases/RecentPurchasesPanel";
import { EXAM_TYPE_OPTIONS } from "./components/examPurchases/examUtils";
import { useAdminDashboard, useAdminExamPurchases } from "../store/admin";
import { DashboardSkeleton } from "./components/common/AdminSkeleton";
import {htmlToText} from "../utils/toDisplayString";

const AdminDashboard = () => {
  const [stats, setStats] = React.useState([]);

  const {
    dashboard: data,
    dashboardLoading: isLoading,
    fetchDashboard,
    refreshSession,
  } = useAdminDashboard();

  // Exam overview (GET /api/admin/exam-dashboard) with its own Exam Type filter.
  const {
    examDashboard,
    examDashboardLoading,
    fetchExamDashboard,
  } = useAdminExamPurchases();

  const [examType, setExamType] = React.useState("");

  React.useEffect(() => {
    fetchExamDashboard({
      recentPurchasesLimit: 10,
      recentFailedEmailsLimit: 10,
      examType,
    });
  }, [fetchExamDashboard, examType]);

  React.useEffect(() => {
    // Refresh the admin session/token on page load (best-effort, non-blocking).
    refreshSession?.();
  }, [refreshSession]);

  React.useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Revalidate when the tab regains focus so changes made elsewhere show up.
  // While the cached snapshot is still fresh this sends no request.
  React.useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState !== "visible") return;
      fetchDashboard();
      fetchExamDashboard({
        recentPurchasesLimit: 10,
        recentFailedEmailsLimit: 10,
        examType,
      });
    };
    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
    };
  }, [fetchDashboard, fetchExamDashboard, examType]);

  React.useEffect(() => {
    const s = data.statistics || data;
    if (data && Object.keys(data).length > 0) {
      setStats([
        {
          title: "Resource persons",
          value: s.totalResourcePersons,
          icon: <FaUserFriends className="text-warning fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Enrolled Students",
          value: s.totalStudents,
          icon: <FaUserGraduate className="text-info fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Staff",
          value: s.totalStaff,
          icon: <FaUsers className="text-primary fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Courses",
          value: s.totalCourses,
          icon: <FaBook className="text-success fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Books",
          value: s.totalBooks,
          icon: <FaBookOpen className="text-warning fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Journals",
          value: s.totalJournals,
          icon: <FaBookOpen className="text-warning fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Project Topics",
          value: s.totalProjectTopics,
          icon: <FaLightbulb className="text-primary fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Seminars",
          value: s.totalSeminarTopics,
          icon: <FaBookOpen className="text-warning fs-3" />,
          bg: "bg-light"
        },
        {
          title: "CBT Subscribers",
          value: s.totalCBTSubscribers,
          icon: <FaLaptop className="text-info fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Purchases",
          value: s.totalPurchases,
          icon: <FaChartLine className="text-warning fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Total Revenue",
          value: s.totalRevenue != null ? `₦${Number(s.totalRevenue).toLocaleString()}` : "₦0",
          icon: <FaDollarSign className="text-success fs-3" />,
          bg: "bg-light"
        },
        {
          title: "Pending Withdrawals",
          value: s.pendingWithdrawals,
          icon: <FaHourglass className="text-danger fs-3" />,
          bg: "bg-light"
        }
      ]);
    }
  }, [data]); 

  return (
    <div className="min-h-full bg-slate-50">
      <AdminNavBar title="Dashboard Overview" />

      {/* Offset to sit beside the fixed sidebar on desktop, full width on mobile */}
      <main className="md:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
          {isLoading ? (
            <DashboardSkeleton />
          ) : (
            <div className="space-y-2">
              {/* Scroll to top */}
              <button
                className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-colors hover:bg-blue-700 md:right-8"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                aria-label="Scroll to top"
              >
                <FontAwesomeIcon icon={faAngleUp} />
              </button>

              <div className="row g-4">
                {stats.map((stat, index) => (
                  <div className="col-md-6 col-xl-3" key={index}>
                    <div className={`card shadow-sm ${stat.bg}`}>
                      <div className="card-body d-flex align-items-center">
                        <div className="me-3">{stat.icon}</div>
                        <div>
                          <h6 className="mb-0">{stat.title}</h6>
                          <h4 className="fw-bold">{stat.value}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Exam Overview (GET /api/admin/exam-dashboard) */}
              <section className="mb-4 mt-6">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-xs font-bold uppercase tracking-wide text-slate-700">
                    Exam Overview
                  </h2>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="dashboardExamType"
                      className="text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                    >
                      Exam Type
                    </label>
                    <select
                      id="dashboardExamType"
                      className="form-select form-select-sm"
                      style={{ minWidth: "140px" }}
                      value={examType}
                      onChange={(e) => setExamType(e.target.value)}
                    >
                      <option value="">All Exam Types</option>
                      {EXAM_TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <ExamSystemAlerts alerts={examDashboard?.alerts} />
                </div>
                <ExamDashboardView
                  dashboard={examDashboard}
                  loading={examDashboardLoading}
                  hideRecentPurchases
                />
              </section>

              <div className="row g-4">
                {/* Recent Registrations */}
                <div className="col-md-6 col-lg-4">
                  <div className="card shadow-sm h-100">
                    <div className="card-body">
                      <h6 className="card-title">
                        <FaUser className="me-2 text-primary" /> Recently Registered Users
                      </h6>
                      <p className="text-muted small">Latest sign-ups</p>

                      {data.recentActivity?.recentRegistrations &&
                        data.recentActivity.recentRegistrations.length !== 0 ? (
                        <ul className="list-group">
                          {data.recentActivity.recentRegistrations.map((user) => (
                            <li
                              key={user.id}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div>
                                <div className="fw-semibold">{htmlToText(user.name)}</div>
                                <div className="text-muted small">{htmlToText(user.email)}</div>
                              </div>
                              {user.createdAt && (
                                <span className="text-muted small">
                                  {new Date(user.createdAt).toLocaleDateString()}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-muted small">No recent registrations</span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Course Completion Rate */}
                <div className="col-md-6 col-lg-4">
                  <div className="card shadow-sm h-100">
                    <div className="card-body">
                      <h6 className="card-title">
                        <FaCheckCircle className="me-2 text-success" /> Course Completion Rate
                      </h6>
                      <p className="text-muted small">Across all active courses</p>

                      <div className="mb-2">
                        <span className="d-block">Overall Completion</span>
                        {data.totalProgresses && data.totalProgresses !== 0 ? (
                          <div className="progress">
                            <div
                              className="progress-bar bg-success"
                              style={{
                                width: `${((data.completedProgresses / data.totalProgresses) * 100).toFixed(2)}%`
                              }}
                            >
                              {((data.completedProgresses / data.totalProgresses) * 100).toFixed(2)}%
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted small">No data to show</span>
                        )}
                      </div>

                      <div>
                        <span className="d-block">Active Students Completing Courses</span>
                        {data.totalStudents && data.totalStudents !== 0 ? (
                          <div className="progress">
                            <div
                              className="progress-bar bg-info"
                              style={{
                                width: `${((data.completedUsers / data.totalStudents) * 100).toFixed(2)}%`
                              }}
                            >
                              {((data.completedUsers / data.totalStudents) * 100).toFixed(2)}%
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted small">No data to show</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Most Active Courses */}
                <div className="col-md-6 col-lg-4 ">
                  <div className="card shadow-sm h-100">
                    <div className="card-body">
                      <h6 className="card-title">
                        <FaBookOpen className="me-2 text-info" /> Most Active Courses
                      </h6>
                      <ul className="list-group list-group-flush mt-2">
                        {data.topCompletedCourses && data.topCompletedCourses.length !== 0 ? (
                          data.topCompletedCourses.map((course) => (
                            <li
                              key={`topCourse-${course.id}`}
                              className="list-group-item d-flex justify-content-between"
                            >
                              <span>{htmlToText(course.title)}</span>
                              <span>📘 {course.completions} views</span>
                            </li>
                          ))
                        ) : (
                          <span className="text-muted small">No data to show</span>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
                {/* Most Active Students */}
                <div className="col-md-12 col-lg-6">
                  <div className="card shadow-sm h-100">
                    <div className="card-body">
                      <h6 className="card-title">
                        <FaUser className="me-2 text-warning" /> Most Active Students
                      </h6>
                      {data.activeUsers && (
                        <ul className="list-group list-group-flush mt-2">
                          {data.activeUsers.map((user) => (
                            <li
                              key={`activeUser-${user.name}`}
                              className="list-group-item d-flex justify-content-between"
                            >
                              <span>{htmlToText(user.name)}</span>
                              <span>
                                🔥 {user.logins} {user.logins === 1 ? "login" : "logins"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Purchases — pinned to the very bottom of the dashboard */}
              <RecentPurchasesPanel purchases={examDashboard?.recent?.purchases} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
